import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2 } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SpecialOrderModal({ open, onClose, product, defaultSize = "unique" }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    message: "",
  });
  const [size] = useState(defaultSize);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  if (!product) return null;

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/orders/special`, {
        product_slug: product.slug,
        size,
        quantity: qty,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone || undefined,
        message: form.message || undefined,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDone(false);
    setError("");
    setForm({ customer_name: "", customer_email: "", customer_phone: "", message: "" });
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[80] bg-black/50"
            onClick={handleClose}
            data-testid="special-order-overlay"
          />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[81] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              data-testid="special-order-modal"
              className="bg-[#FBF8EF] w-full max-w-xl max-h-[92vh] overflow-y-auto pointer-events-auto"
              data-lenis-prevent
            >
              <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-[#E5DFD3]">
                <p className="text-[11px] tracking-[0.32em] uppercase text-[#121212]">
                  {t("specialOrder.title")}
                </p>
                <button
                  onClick={handleClose}
                  data-testid="special-order-close"
                  aria-label={t("specialOrder.close")}
                  className="text-[#121212] -mr-2 p-2"
                >
                  <X size={20} strokeWidth={1.2} />
                </button>
              </div>

              {done ? (
                <div className="px-6 md:px-10 py-16 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 border border-[#5C4033] rounded-full mb-6">
                    <Check size={20} strokeWidth={1.2} className="text-[#5C4033]" />
                  </div>
                  <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-tight">
                    {t("specialOrder.sent")}
                  </p>
                  <p className="mt-4 text-sm text-[#4A4641] leading-[1.8] max-w-sm mx-auto">
                    {product.status === "soon"
                      ? t("specialOrder.sentSubSoon", { defaultValue: "Nous vous écrivons dès que cette pièce arrive en atelier." })
                      : t("specialOrder.sentSubOrder", { defaultValue: "L'atelier vous écrit dans les jours qui viennent. Comptez 3 à 4 semaines pour la confection." })}
                  </p>
                  <button
                    onClick={handleClose}
                    data-testid="special-order-done"
                    className="mt-10 h-11 px-8 bg-[#121212] text-[#FBF8EF] text-[11px] tracking-[0.32em] uppercase"
                  >
                    {t("specialOrder.close")}
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="px-6 md:px-8 py-7 space-y-5">
                  <div className="border-b border-[#E5DFD3] pb-4">
                    <p className="font-serif text-2xl text-[#121212]">{product.name}</p>
                    <p className="text-[11px] tracking-[0.28em] uppercase text-[#5C4033] mt-1">
                      {product.status === "soon" ? t("specialOrder.subtitle") : t("product.fabricationNote")}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] tracking-[0.28em] uppercase text-[#5C4033] block mb-2">
                        {t("product.sizeLabel")}
                      </label>
                      <div
                        data-testid="special-size-unique"
                        className="h-11 flex items-center justify-center border border-[#E5DFD3] text-[11px] tracking-[0.28em] uppercase bg-[#F3EFE7] text-[#121212]"
                      >
                        {t("cart.sizeUnique")}
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.28em] uppercase text-[#5C4033] block mb-2">
                        {t("checkout.quantity")}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={qty}
                        onChange={(e) => setQty(parseInt(e.target.value) || 1)}
                        data-testid="special-qty"
                        className="w-full h-11 border border-[#E5DFD3] bg-transparent px-3 text-sm focus:outline-none focus:border-[#121212]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      required
                      placeholder={t("specialOrder.name")}
                      value={form.customer_name}
                      onChange={onChange("customer_name")}
                      data-testid="special-name"
                      className="h-11 bg-transparent border-b border-[#5C4033]/30 focus:border-[#121212] outline-none text-[#121212] font-serif"
                    />
                    <input
                      required
                      type="email"
                      placeholder={t("specialOrder.email")}
                      value={form.customer_email}
                      onChange={onChange("customer_email")}
                      data-testid="special-email"
                      className="h-11 bg-transparent border-b border-[#5C4033]/30 focus:border-[#121212] outline-none text-[#121212] font-serif"
                    />
                  </div>
                  <input
                    type="tel"
                    placeholder={t("specialOrder.phone")}
                    value={form.customer_phone}
                    onChange={onChange("customer_phone")}
                    data-testid="special-phone"
                    className="w-full h-11 bg-transparent border-b border-[#5C4033]/30 focus:border-[#121212] outline-none text-[#121212] font-serif"
                  />
                  <textarea
                    rows={3}
                    placeholder={t("specialOrder.message")}
                    value={form.message}
                    onChange={onChange("message")}
                    data-testid="special-message"
                    className="w-full bg-transparent border-b border-[#5C4033]/30 focus:border-[#121212] outline-none text-[#121212] font-serif py-2 resize-none"
                  />

                  {error && (
                    <p className="text-sm text-[#A33A2A]" data-testid="special-error">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="special-submit"
                    className="w-full h-12 bg-[#121212] text-[#FBF8EF] text-[11px] tracking-[0.32em] uppercase hover:bg-[#5C4033] transition-colors duration-500 disabled:opacity-60"
                  >
                    {loading ? (
                      <span className="inline-flex items-center gap-2 justify-center">
                        <Loader2 size={14} className="animate-spin" /> {t("specialOrder.sending")}
                      </span>
                    ) : (
                      t("specialOrder.submit")
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
